import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../../GRI300/GRI308/SectionHeading";
import CustomTextField from "../../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../../Components/Common/ReviewBox";
import { reviewGRI } from "../../../../utils/companyId";
import { currentYear } from "../../../../utils/generateYearRanges";

const GRI403_2 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    detailedOverview: {
      procedureEmployed: "",
      ensuredEffectiveness: "",
      outcome: "",
    },

    workerHazardReportingMechanisms: "",
    workerSafetyWithdrawalPolicies: "",
    incidentInvestigationProcedures: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        const data = response.data.data.GRI400?.hazardIdentification || {};
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);

        setFormData({
          detailedOverview: {
            procedureEmployed: data.detailedOverview?.procedureEmployed || "",
            outcome: data.detailedOverview?.outcome || "",
            ensuredEffectiveness:
              data.detailedOverview?.ensuredEffectiveness || "",
          },
          workerHazardReportingMechanisms:
            data.workerHazardReportingMechanisms || "",
          workerSafetyWithdrawalPolicies:
            data.workerSafetyWithdrawalPolicies || "",
          incidentInvestigationProcedures:
            data.incidentInvestigationProcedures || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate sector

    // Validate required fields in the formData
    if (!formData.detailedOverview.procedureEmployed) {
      newErrors.procedureEmployed = "This field is required";
      formIsValid = false;
    }

    if (!formData.detailedOverview.ensuredEffectiveness) {
      newErrors.ensuredEffectiveness = "This field is required";
      formIsValid = false;
    }

    if (!formData.detailedOverview.outcome) {
      newErrors.outcome = "This field is required";
      formIsValid = false;
    }

    if (!formData.workerHazardReportingMechanisms) {
      newErrors.workerHazardReportingMechanisms = "This field is required";
      formIsValid = false;
    }

    if (!formData.workerSafetyWithdrawalPolicies) {
      newErrors.workerSafetyWithdrawalPolicies = "This field is required";
      formIsValid = false;
    }

    if (!formData.incidentInvestigationProcedures) {
      newErrors.incidentInvestigationProcedures = "This field is required";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      // if (!validateForm()) return;
      const payload = {
        type: "GRI400",
        hazardIdentification: {
          ...formData,
        },
        submitted403_2: true,
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/403-3/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/403-3/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {/* <SectionHeading text="2-1 Activities, value chain and other business relationships" /> */}
      {!hide && (
        <p className={styles.section_heading}>
          403 : Occupational Health and Safety
        </p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        403-2 : Hazard identification, risk assessment, and incident
        investigation
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}
        <SectionHeading text="a. Could you provide a detailed overview of the procedures employed by the organization to identify both routine and non-routine work-related hazards, assess associated risks, and utilize the hierarchy of controls to eliminate hazards and mitigate risks?" />
        <CustomTextField
          value={formData.detailedOverview.procedureEmployed}
          onChange={(e) =>
            handleNestedInputChange(
              "detailedOverview",
              "procedureEmployed",
              e.target.value
            )
          }
          error={errors.procedureEmployed}
          helperText={errors.procedureEmployed}
          disabled={disabled}
        />

        <Box sx={{ marginLeft: "1rem" }}>
          <SectionHeading text="i. How does the organization ensure the effectiveness and quality of these processes, particularly concerning the competency of individuals involved?" />
          <CustomTextField
            value={formData.detailedOverview.ensuredEffectiveness}
            onChange={(e) =>
              handleNestedInputChange(
                "detailedOverview",
                "ensuredEffectiveness",
                e.target.value
              )
            }
            error={errors.ensuredEffectiveness}
            helperText={errors.ensuredEffectiveness}
            disabled={disabled}
          />

          <SectionHeading text="ii. How are the outcomes of these processes utilized to assess and continuously enhance the occupational health and safety management system? " />
          <CustomTextField
            value={formData.detailedOverview.outcome}
            // onChange={(e) => handleNestedInputChange("outcome", e.target.value)}
            onChange={(e) =>
              handleNestedInputChange(
                "detailedOverview",
                "outcome",
                e.target.value
              )
            }
            error={errors.outcome}
            helperText={errors.outcome}
            disabled={disabled}
          />
        </Box>

        {/* Section B */}
        <SectionHeading text="b. Please describe the mechanisms in place for workers to report work-related hazards and hazardous situations. Additionally, explain the safeguards implemented to protect workers against any potential reprisals." />
        <CustomTextField
          value={formData.workerHazardReportingMechanisms}
          onChange={(e) =>
            handleInputChange("workerHazardReportingMechanisms", e.target.value)
          }
          error={errors.workerHazardReportingMechanisms}
          helperText={errors.workerHazardReportingMechanisms}
          disabled={disabled}
        />

        <SectionHeading text="c. Could you outline the organization's policies and procedures enabling workers to remove themselves from work environments they perceive as potentially hazardous to their health or safety? Furthermore, elucidate the measures in place to safeguard workers from potential reprisals." />
        <CustomTextField
          value={formData.workerSafetyWithdrawalPolicies}
          onChange={(e) =>
            handleInputChange("workerSafetyWithdrawalPolicies", e.target.value)
          }
          error={errors.workerSafetyWithdrawalPolicies}
          helperText={errors.workerSafetyWithdrawalPolicies}
          disabled={disabled}
        />

        <SectionHeading text="d. Describe the procedures utilized to investigate work-related incidents, encompassing the identification of hazards and risk assessment related to the incidents, determination of corrective actions through the hierarchy of controls, and identification of necessary improvements to the occupational health and safety management system." />
        <CustomTextField
          value={formData.incidentInvestigationProcedures}
          onChange={(e) =>
            handleInputChange("incidentInvestigationProcedures", e.target.value)
          }
          error={errors.incidentInvestigationProcedures}
          helperText={errors.incidentInvestigationProcedures}
          disabled={disabled}
        />

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI403_2;
